/**
 * 이상한 방 - 매치 세션의 받는 문 (`ServerMessage` 하나 → 뷰 교체 · `seq` 문 · 시계 앵커 · 거절 짝짓기)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것 - 두 방과 뷰) · 11.3절(서버가 쥐는 것)
 *            로드맵 M2(매치 세션 - 세 메시지 세 자리 · 로비는 스스로 지난다) · M4 준비 몫(`matchSession.ts` 400줄 분할 - 동작 변화 0)
 *
 * `matchSession.ts`에서 갈라져 나왔다. 전송(`Transport`)에서 온 메시지를 **어디에 꽂을지**만 정한다 - 뷰는 세션의 것이라
 * 콜백(`setView`)으로 돌려주고, 효과는 번역기(`matchSessionEffects.ts`)에, 방은 씬(`matchSessionScene.ts`)에, 시간은
 * 시계(`matchClock.ts`)에 넘긴다. 여기에도 **규칙이 하나도 없다** - 메시지의 뜻은 프로토콜이, 그 뜻의 화면은 저 셋이 정한다.
 *
 * ## 세 메시지, 세 자리
 *
 *  - `rooms`  두 방을 복원해 든다(`matchSessionScene.ts`). 씬 갱신은 여기서 곧바로 하지 않는다 - 언제 무엇을 보일지는
 *             뒤따르는 `step`의 `warpTo`·전환이 정한다(호스트가 `rooms`를 `step`보다 먼저 보내는 이유). 첫 방만 예외다.
 *  - `step`   뷰를 갈아 끼우고 효과를 순서대로 번역한다. `seq` 문(`createSeqGate`)을 `rooms`와 **하나로** 지난다.
 *  - `clock`  2Hz. 뷰의 시간 값은 `step.atMs`의 것이라 `matchClock`이 프레임 델타로 잇고 이 메시지로 보정한다.
 *
 * ## 로비는 세션 밖이다 (M6)
 *
 * M2~M5에는 로비 화면이 없어 이 문이 `welcome`에 `ready`를, 전원 준비면 `start`를 보냈다. M6에서 그 두 줄은 로비
 * (`app/lobbyFlow.ts` - 사람 방은 화면, 봇 판은 자동 진행)로 갔다. 세션에 남겨 두면 소켓 방에서 사람이 [준비]를 누르기 전에
 * 세션이 대신 눌러 버린다. 세션은 로비 페이즈에 아무것도 보내지 않고, 로비가 세션의 `act`를 빌려 보낸다(`cseq`가 하나여야 한다).
 *
 * ## `welcome`은 두 번 올 수 있다 - 앉을 때와 돌아올 때
 *
 * 재접속(`matchHost.reconnect`)의 `welcome`에는 효과가 없다 - 끊긴 동안의 `phaseEnter`는 지나갔다. 뷰와 방은 그대로 받으므로
 * HUD·씬은 맞지만 제거 프록시(`syncProxies` 효과가 세운다)는 없다. 그래서 `welcome`마다 프록시를 두 방의 차집합으로 다시 세운다 -
 * 규칙이 아니라 화면의 물건이고(무엇이 제거됐는지는 두 방을 가진 클라이언트가 이미 아는 것, 11.2절), 방이 없으면 아무 일도 없다.
 *
 * ## 거절은 보낸 쪽이 아니라 받는 쪽이 짝짓는다
 *
 * `rejected{cseq}`는 어느 요청의 것인지만 말한다. `shoot`이었으면 판정이 오지 않을 탭을 기다리는 줄(`pendingShots`)에서 뺀다 -
 * 안 빼면 다음 판정이 엉뚱한 자리에 찍힌다. 보내는 쪽(`send`)은 세션에 남아 있다 - 입력 번역(`matchSessionInput.ts`)과 결과
 * 화면의 [한 판 더], 로비의 [준비]·[시작]도 같은 문을 쓴다.
 */

import { t } from '../i18n'
import { createSeqGate, type ServerMessage } from '../net/protocol'
import type { Transport } from '../net/transport'
import type { GameHudHandle } from '../ui/gameHud'
import type { PlayerEvent } from './match/matchTypes'
import type { ClientView } from './match/matchView'
import type { MatchClock } from './matchClock'
import type { ChangerLink } from './matchSessionChanger'
import type { DecorateLink } from './matchSessionDecorate'
import type { MatchEffectHandler, PendingShot } from './matchSessionEffects'
import type { MatchScene } from './matchSessionScene'

/* --- 타입 ------------------------------------------------------------------- */

/** 받는 문이 세션에서 빌려 쓰는 것 전부. 세션 클로저가 아니라 **이 인자만** 본다 - 옮긴 것이 배선임이 드러나게. */
export type MatchInboxParts = Readonly<{
  transport: Transport
  clock: MatchClock
  scene: MatchScene
  effects: MatchEffectHandler
  hud: GameHudHandle
  /** 판정을 기다리는 탭들(도착 순). 거절된 발을 여기서 뺀다. */
  pendingShots: PendingShot[]
  /** 새 뷰. `welcome`·`step`이 온 뒤, 효과를 번역하기 **전에** 올린다(번역기는 새 뷰를 읽는다). */
  setView: (view: ClientView) => void
  /** 이벤트 하나를 호스트로. 돌려주는 `cseq`로 거절을 짝짓는다(세션의 `send`). */
  send: (event: PlayerEvent) => number
  /** 뷰가 바뀐 뒤 HUD·참가자 줄·커서를 다시 그린다(세션의 `render`). */
  render: () => void
  /** 지금 서 있는 체인저 도구(M4). 없으면 `null` - 거절된 요청과 새 뷰를 그쪽에도 알린다(`matchSessionChanger.ts`). */
  changer: () => ChangerLink | null
  /** 지금 서 있는 꾸미기 도구(M10). 새 뷰로 남은 개수를 다시 읽는다 - 체인저와 같은 자리, 같은 이유다. */
  decorator: () => DecorateLink | null
}>

export type MatchInbox = Readonly<{
  /** 전송 구독을 푼다. 전송을 닫는 것은 세션이다(`MatchSessionDeps.transport` 주석). */
  dispose: () => void
}>

/* --- 본체 ------------------------------------------------------------------- */

export function createMatchInbox(parts: MatchInboxParts): MatchInbox {
  const { transport, clock, scene, effects, hud, pendingShots } = parts

  const seqGate = createSeqGate()

  function onMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        parts.setView(msg.view)
        scene.receive(msg.rooms)
        // 돌아온 자리의 제거 프록시(머리말). 방이 아직 없으면(로비) 번역기가 그냥 지나간다.
        effects.applyStep([{ type: 'syncProxies' }])
        parts.render()
        return
      case 'rooms':
        if (!seqGate.accept(msg.seq)) return
        scene.receive(msg)
        return
      case 'step':
        if (!seqGate.accept(msg.seq)) return
        clock.onStep(msg.atMs, msg.phaseElapsedMs)
        parts.setView(msg.view)
        effects.applyStep(msg.effects)
        // 도구는 뷰의 예산·건드린 목록으로 잠금을 다시 센다. 효과 뒤에 부른다 - 수용 신호가 대기 줄을 먼저 비워야 한다.
        parts.changer()?.onView()
        parts.decorator()?.onView()
        parts.render()
        return
      case 'clock':
        clock.onClock(msg.phaseElapsedMs)
        return
      case 'rejected': {
        // 거절된 사격은 판정이 오지 않는다 - 기다리는 자리에서 빼야 다음 판정이 엉뚱한 자리에 찍히지 않는다.
        const at = pendingShots.findIndex((shot) => shot.cseq === msg.cseq)
        if (at >= 0) pendingShots.splice(at, 1)
        // 리듀서 게이트에 걸린 체인저 요청(예산 초과 · 페이즈 밖)은 `changeRejected`가 아니라 이 길로 온다 - 초안을 되돌린다.
        parts.changer()?.onRequestDropped(msg.cseq)
        return
      }
      case 'error':
        // 서버가 끊었다(다른 창이 좌석을 가져갔다 · 버전이 다르다) - 이 세션이 고칠 수 있는 것이 아니다. 사람에게 한 줄(`net.error.*`,
        // 연결 배너와 같은 사전), 원문은 콘솔에.
        console.error('[matchSession] 호스트가 끊었다', msg.code)
        hud.showNotice(t(`net.error.${msg.code}`), 'warn')
        return
      case 'presence':
      case 'pong':
        return
    }
  }

  const unsubscribe = transport.onMessage(onMessage)

  return { dispose: unsubscribe }
}
